import React from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { memo } from '~/ui/component'
import { DateTimeRangeDisplay, HBox, Label, SVG, TextBlock, VBox } from '~/ui/components'
import { layout } from '~/ui/styling'
import { BeWizrEnrollmentStatus, BeWizrTile } from '../bewizr'

export interface Props {
  meetingID:        string
  occurrenceID?:    string
  label?:           string
  title:            string
  startDate?:       DateTime
  endDate?:         DateTime
  isEnrolled?:      boolean
  isOnWaitingList?: boolean
  capacity?:        number | null
  placesLeft?:      number | null
  mayEnroll?:       boolean
  image:            string | null
  horizontal?:      boolean
  locked?:          boolean
  description?:     string | null
}

const BeWizrMeetingTile = memo('BeWizrMeetingTile', (props: Props) => {

  const {
    meetingID,
    occurrenceID,
    label,
    title,
    placesLeft = null,
    mayEnroll = false,
    isEnrolled = false,
    isOnWaitingList = false,
    startDate,
    endDate,
    image,
    description,
    horizontal,
    locked,
  } = props

  const [t] = useTranslation('bewizr-meetings')

  const href = React.useMemo(() => {
    const baseHref = `//bewizr-meeting/${meetingID}`
    if (occurrenceID == null) { return baseHref }

    return `${baseHref}/occurrences/${occurrenceID}`
  }, [meetingID, occurrenceID])

  //------
  // Rendering

  function render() {
    return (
      <BeWizrTile
        icon='calendar'
        label={label}
        detail={renderInfo()}
        title={title}
        subtitle={renderStatus()}
        href={href}
        image={image}
        buttonCaption={t('view')}
        horizontal={horizontal}
        locked={locked}
        children={renderDescription()}
      />
    )
  }

  function renderInfo() {
    if (startDate == null || endDate == null) {
      return (
        <HBox gap={layout.padding.inline.s}>
          <SVG name='calendar' dim size={layout.icon.s}/>
          <Label tiny>
            {t('dates.multiple')}
          </Label>
        </HBox>
      )
    } else {
      return (
        <VBox gap={layout.padding.inline.xs} align='left'>
          <HBox gap={layout.padding.inline.s}>
            <SVG name='calendar' dim size={layout.icon.s}/>
            <DateTimeRangeDisplay
              start={startDate}
              end={endDate}
              timeFormat={false}
              tiny
            />
          </HBox>
          <HBox gap={layout.padding.inline.s}>
            <SVG name='clock' dim size={layout.icon.s}/>
            <DateTimeRangeDisplay
              start={startDate}
              end={endDate}
              dateFormat={false}
              tiny
            />
          </HBox>
        </VBox>
      )
    }
  }

  function renderStatus() {
    if (!isEnrolled && !isOnWaitingList && !mayEnroll) { return null }

    return (
      <BeWizrEnrollmentStatus
        isEnrolled={isEnrolled}
        isOnWaitingList={isOnWaitingList}
        placesLeft={placesLeft}
        small
      />
    )
  }

  function renderDescription() {
    if (description == null) { return null }

    return (
      <TextBlock small dim>
        {description}
      </TextBlock>
    )
  }

  return render()

})

export default BeWizrMeetingTile